import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from '../../components/common/Modal';
import { shippingApi } from '../../api/shippingApi';
import { parseDirectAddress } from './directAddressUtils';
import { allowsArrivalDateInput, allAllowArrivalDateInput } from './shukkaDateRules';

function dash(v) {
  if (v == null || String(v).trim() === '') return '';
  return String(v);
}

/**
 * 一括編集モーダル（参照画面・社内専用）。
 * 同じ受注No × 仕入先 × 送り先 × 発送日の単位で編集する。
 */
export function ShipmentClusterEditModal({
  isOpen,
  anchorRow,
  listItems = [],
  onClose,
  onSaved,
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const [directName, setDirectName] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [directAddress, setDirectAddress] = useState('');
  const [directTel, setDirectTel] = useState('');
  const [biko, setBiko] = useState('');
  const [shareMemo, setShareMemo] = useState('');
  const [shipmentDate, setShipmentDate] = useState('');
  const [arrivalDate, setArrivalDate] = useState('');

  const [touched, setTouched] = useState(() => new Set());
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());

  const markTouched = (field) => {
    setTouched((prev) => {
      const next = new Set(prev);
      next.add(field);
      return next;
    });
  };

  const loadPreview = useCallback(async () => {
    if (!anchorRow) return;
    setLoading(true);
    setError(null);
    try {
      const data = await shippingApi.previewJuchuShareOverride({
        hacchu_code: anchorRow.hacchu_code,
        hacchu_changecnt: anchorRow.hacchu_changecnt,
        hacchu_datacode: anchorRow.hacchu_datacode,
      });
      setPreview(data);
      const parsed = parseDirectAddress(data.direct_address, data.direct_tel);
      setDirectName(dash(data.direct_name));
      setPostalCode(dash(data.postal_code || parsed.postalCode));
      setDirectAddress(dash(parsed.address || data.direct_address));
      setDirectTel(dash(parsed.tel || data.direct_tel));
      setBiko(dash(data.biko));
      setShareMemo(dash(data.share_memo));
      setShipmentDate(data.shipment_date ? String(data.shipment_date).slice(0, 10) : '');
      setArrivalDate('');
      setTouched(new Set());
      const initial = new Set();
      (data.date_candidates || []).forEach((c) => {
        if (c.date_selectable) {
          initial.add(`${c.hacchu_code}:${c.hacchu_datacode}`);
        }
      });
      setSelectedKeys(initial);
    } catch (e) {
      setPreview(null);
      setError(e?.response?.data?.detail || e?.message || 'プレビューの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [anchorRow]);

  useEffect(() => {
    if (!isOpen || !anchorRow) {
      setPreview(null);
      setHistory([]);
      setShowHistory(false);
      setError(null);
      return;
    }
    loadPreview();
  }, [isOpen, anchorRow, loadPreview]);

  const selectableCandidates = useMemo(
    () => (preview?.date_candidates || []).filter((c) => c.date_selectable),
    [preview]
  );

  const showArrivalInput = useMemo(() => {
    if (!allowsArrivalDateInput(anchorRow?.shukka_type)) {
      return false;
    }
    const candidates = preview?.date_candidates || [];
    if (candidates.length === 0) {
      return allowsArrivalDateInput(anchorRow?.shukka_type);
    }
    const types = candidates.map((c) => {
      const match = (listItems || []).find(
        (row) =>
          row.hacchu_code === c.hacchu_code &&
          Number(row.hacchu_datacode) === Number(c.hacchu_datacode)
      );
      return match?.shukka_type ?? anchorRow?.shukka_type;
    });
    return allAllowArrivalDateInput(types);
  }, [anchorRow, preview, listItems]);

  const toggleKey = (c) => {
    if (!c.date_selectable) return;
    const key = `${c.hacchu_code}:${c.hacchu_datacode}`;
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const loadHistory = async () => {
    if (!preview?.juchu_code && !preview?.juchu_no) return;
    try {
      const rows = await shippingApi.getJuchuShareOverrideHistory({
        juchu_code: preview.juchu_code,
        juchu_edaban: preview.juchu_edaban,
        juchu_no: preview.juchu_no,
      });
      setHistory(Array.isArray(rows) ? rows : []);
      setShowHistory(true);
    } catch (e) {
      alert(e?.response?.data?.detail || e?.message || '履歴の取得に失敗しました');
    }
  };

  const handleResetToCore = async () => {
    if (!anchorRow) return;
    if (!window.confirm('この送り先の Web 上書きを消し、基幹の値に戻しますか？')) return;
    setSaving(true);
    setError(null);
    try {
      await shippingApi.batchJuchuShareOverride({
        hacchu_code: anchorRow.hacchu_code,
        hacchu_changecnt: anchorRow.hacchu_changecnt,
        hacchu_datacode: anchorRow.hacchu_datacode,
        apply_fields: [],
        reset_destination_to_core: true,
      });
      if (onSaved) onSaved({ reset: true, anchorRow, preview });
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || '基幹値への復帰に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!anchorRow || !preview) return;
    const applyFields = [];
    const payload = {
      hacchu_code: anchorRow.hacchu_code,
      hacchu_changecnt: anchorRow.hacchu_changecnt,
      hacchu_datacode: anchorRow.hacchu_datacode,
      apply_fields: applyFields,
    };

    const destMap = {
      direct_name: directName,
      postal_code: postalCode,
      direct_address: directAddress,
      direct_tel: directTel,
      biko,
      share_memo: shareMemo,
    };
    Object.entries(destMap).forEach(([field, value]) => {
      if (touched.has(field)) {
        applyFields.push(field);
        payload[field] = value;
      }
    });

    if (touched.has('shipment_date') || (showArrivalInput && touched.has('arrival_date'))) {
      if (selectedKeys.size === 0) {
        setError('発送日・着日を更新する場合は、分納なしの明細を選択してください');
        return;
      }
      if (touched.has('shipment_date')) {
        applyFields.push('shipment_date');
        payload.shipment_date = shipmentDate || null;
      }
      if (showArrivalInput && touched.has('arrival_date')) {
        applyFields.push('arrival_date');
        payload.arrival_date = arrivalDate || null;
      }
      payload.selected_date_targets = (preview.date_candidates || [])
        .filter((c) => selectedKeys.has(`${c.hacchu_code}:${c.hacchu_datacode}`))
        .map((c) => ({
          hacchu_code: c.hacchu_code,
          hacchu_changecnt: c.hacchu_changecnt,
          hacchu_datacode: c.hacchu_datacode,
        }));
    }

    if (applyFields.length === 0) {
      setError('変更した項目がありません');
      return;
    }

    if (
      (touched.has('shipment_date') || (showArrivalInput && touched.has('arrival_date'))) &&
      !window.confirm(
        '発送日を変えると、一括の対象から外れる明細があります。保存しますか？'
      )
    ) {
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const result = await shippingApi.batchJuchuShareOverride(payload);
      if (onSaved) {
        onSaved({
          result,
          anchorRow,
          preview,
          payload,
          applyFields,
        });
      }
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      title={
        <div>
          <div className="text-lg font-semibold text-slate-900">一括編集</div>
          <div className="text-xs text-slate-500 mt-1">
            同じ受注No・仕入先・送り先・発送日の単位でまとめます
          </div>
        </div>
      }
    >
      {loading ? (
        <div className="py-8 text-center text-slate-500">読み込み中...</div>
      ) : error && !preview ? (
        <div className="text-red-600 text-sm">{error}</div>
      ) : preview ? (
        <div className="space-y-4">
          <div className="rounded border border-slate-200 bg-slate-50 p-3 text-sm space-y-1">
            <div>受注No：{preview.juchu_no || '-'}</div>
            <div>仕入先：{preview.supplier_name || '-'}</div>
            <div>
              送り先：
              {preview.destination_empty
                ? '（未設定・この枝番のみ）'
                : preview.direct_name || '-'}
            </div>
            <div>発送日：{preview.shipment_date || '（未設定）'}</div>
            <div className="text-slate-600 pt-1">
              対象：明細 {preview.detail_count}件 ／ 発注 {preview.hacchu_count}件
              ／ 枝番 {preview.edaban_count}件
            </div>
            {!preview.destination_empty && preview.other_ship_date_destination_count > 0 ? (
              <div className="text-amber-800 text-xs">
                住所・備考・メモは、同じ受注・同じ送り先の別発送日にも揃えます（
                {preview.other_ship_date_destination_count}件）
              </div>
            ) : null}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="text-sm">
              <span className="text-slate-600">送り先名</span>
              <input
                className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5"
                value={directName}
                onChange={(e) => {
                  setDirectName(e.target.value);
                  markTouched('direct_name');
                }}
              />
            </label>
            <label className="text-sm">
              <span className="text-slate-600">郵便番号</span>
              <input
                className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5"
                value={postalCode}
                onChange={(e) => {
                  setPostalCode(e.target.value);
                  markTouched('postal_code');
                }}
              />
            </label>
            <label className="text-sm md:col-span-2">
              <span className="text-slate-600">住所</span>
              <input
                className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5"
                value={directAddress}
                onChange={(e) => {
                  setDirectAddress(e.target.value);
                  markTouched('direct_address');
                }}
              />
            </label>
            <label className="text-sm">
              <span className="text-slate-600">電話</span>
              <input
                className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5"
                value={directTel}
                onChange={(e) => {
                  setDirectTel(e.target.value);
                  markTouched('direct_tel');
                }}
              />
            </label>
            <label className="text-sm">
              <span className="text-slate-600">備考</span>
              <input
                className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5"
                value={biko}
                onChange={(e) => {
                  setBiko(e.target.value);
                  markTouched('biko');
                }}
              />
            </label>
            <label className="text-sm md:col-span-2">
              <span className="text-slate-600">メモ</span>
              <textarea
                className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5"
                rows={2}
                value={shareMemo}
                onChange={(e) => {
                  setShareMemo(e.target.value);
                  markTouched('share_memo');
                }}
              />
            </label>
            <label className="text-sm">
              <span className="text-slate-600">発送日（選択明細のみ）</span>
              <input
                type="date"
                className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5"
                value={shipmentDate}
                onChange={(e) => {
                  setShipmentDate(e.target.value);
                  markTouched('shipment_date');
                }}
              />
            </label>
            {showArrivalInput && (
              <label className="text-sm">
                <span className="text-slate-600">着日（選択明細のみ）</span>
                <input
                  type="date"
                  className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5"
                  value={arrivalDate}
                  onChange={(e) => {
                    setArrivalDate(e.target.value);
                    markTouched('arrival_date');
                  }}
                />
              </label>
            )}
          </div>

          <div className="border border-slate-200 rounded">
            <div className="px-3 py-2 bg-slate-50 text-sm font-medium text-slate-800 flex justify-between">
              <span>日付一括の対象明細（分納ありは選択不可）</span>
              <span className="text-slate-500 font-normal">
                選択 {selectedKeys.size}/{selectableCandidates.length}
              </span>
            </div>
            <div className="max-h-40 overflow-y-auto divide-y divide-slate-100">
              {(preview.date_candidates || []).map((c) => {
                const key = `${c.hacchu_code}:${c.hacchu_datacode}`;
                const checked = selectedKeys.has(key);
                return (
                  <label
                    key={key}
                    className={`flex items-start gap-2 px-3 py-2 text-sm ${
                      c.date_selectable ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'
                    }`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      className="mt-1"
                      disabled={!c.date_selectable}
                      checked={checked}
                      onChange={() => toggleKey(c)}
                    />
                    <span>
                      <span className="text-slate-900">
                        {c.hacchu_no || '-'} / {c.product_disp || c.product_name || c.hacchu_datacode}
                      </span>
                      {c.has_split_plans ? (
                        <span className="block text-xs text-amber-700">
                          分納計画あり。日付は行を開いて分納から編集
                        </span>
                      ) : null}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          {preview.core_direct_address || preview.core_biko ? (
            <div className="text-xs text-slate-500">
              基幹の値（参考）：{preview.core_direct_name || ''}{' '}
              {preview.core_direct_address || ''} {preview.core_biko || ''}
            </div>
          ) : null}

          {error ? <div className="text-sm text-red-600">{error}</div> : null}

          {showHistory ? (
            <div className="border border-slate-200 rounded max-h-40 overflow-y-auto text-xs">
              {(history || []).length === 0 ? (
                <div className="p-3 text-slate-500">履歴はありません</div>
              ) : (
                history.map((h) => (
                  <div key={h.history_id} className="px-3 py-2 border-b border-slate-100">
                    <div className="text-slate-700">
                      {h.changed_at} {h.changed_by} / {h.field_label || h.field_name}
                    </div>
                    <div className="text-slate-500">
                      {(h.old_value || '（空）')} → {(h.new_value || '（空）')}
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : null}

          <div className="flex flex-wrap gap-2 justify-end pt-2">
            <button
              type="button"
              className="px-3 py-1.5 text-sm border border-slate-300 rounded text-slate-700"
              onClick={loadHistory}
              disabled={saving}
            >
              履歴
            </button>
            <button
              type="button"
              className="px-3 py-1.5 text-sm border border-slate-300 rounded text-slate-700"
              onClick={handleResetToCore}
              disabled={saving}
            >
              基幹の値に戻す
            </button>
            <button
              type="button"
              className="px-3 py-1.5 text-sm border border-slate-300 rounded text-slate-700"
              onClick={onClose}
              disabled={saving}
            >
              取消
            </button>
            <button
              type="button"
              className="px-3 py-1.5 text-sm rounded bg-slate-800 text-white disabled:opacity-50"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? '保存中...' : '保存'}
            </button>
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
